import React from 'react';

import http from 'api/http';
import { manufacturerAPI, boatAPI, trailerAPI } from 'api/requests';

import { ROLES } from 'constants/roles';

import NotificationManager from 'dom-managers/notification';

import { stopPropagation } from 'helpers';

import Store from '../../../registerIDBWorker';

import WithUser from 'HOC/User';

import Modal from 'Components/Modal';
import { Input, inputStyleClasses } from 'Components/Input';
import Button, { buttonColor, buttonType } from 'Components/Button';

import Header from './Header';
import Footer from './Footer';
import TableBody from './TableBody';
import TableHeader from './TableHeader';

const getSearchableFields = isBoatManufacturer => [
  { fieldName: 'name' },
  { fieldName: 'code' },
  ...(isBoatManufacturer ? [{ fieldName: 'boatBrands', nestedFieldName: 'name', isArrayOfObjects: true }] : []),
];

// TODO: rewrite and move to helpers
const isStringIncludes = (strA, strB) => strA.toLowerCase().includes(strB.toLowerCase());

@WithUser
export default class DashboardScene extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      rowsCount: 10,
      activePage: 1,
      sortedBy: 'name',
      sortOrder: 1,
      manufacturers: [],
      filterState: { active: true },
      additionalFilterState: {},
      searchString: '',
      isModalOpen: '',
      requestInProcess: false,
      listRequestInProcess: true,
      selectedYear: '',
      currentYear: 0,
    };

    const storedState = props.user.getStoredState('dashboardListState', this.state.filterState);
    //props.user.updateURLSearchString(storedState);

    this.state.filterState = {
      ...this.state.filterState,
      ...storedState
    };

    const storedAdditionalState = props.user.getStoredState('dashboardListAdditionalState', this.state.additionalFilterState);

    this.state.additionalFilterState = {
      ...this.state.additionalFilterState,
      ...storedAdditionalState
    };

    this.allManufacturers = [];

    this.isOilDashboard = this.props.match.params.manufacturerRole === ROLES.OIL_MANUFACTURER.nameForUrl;
    this.isWithPaymentStatus = false;
    this.isWithCertificationStatus = false;

    props.user.selectManufacturer(null);
  }

  componentDidMount() {
    this.roleSwitch();

    this.getCurrentYear().then(() => {
      this.loadFromStore();
      this.updateListData();
    });
  }

  componentWillUnmount() {
    this.props.user.saveState('dashboardListState', this.state.filterState, []);
    this.props.user.saveState('dashboardListAdditionalState', this.state.additionalFilterState, []);
  }

  roleSwitch = () => {
    const { user, match } = this.props;

    const roleServerName = Object.values(ROLES).reduce((acc, role) => {
      if (match.params.manufacturerRole === role.nameForUrl) {
        return role.serverName;
      }

      return acc;
    }, null);

    if (user.getActiveRole() !== roleServerName) {
      user.changeMenu(roleServerName);
    }
  };

  getCurrentYear = () => {
    const { user } = this.props;

    const storedSelectedYear = user.getSelectedYear();

    return new Promise((resolve, reject) => {
      switch (user.getActiveRole()) {
        case ROLES.BOAT_MANUFACTURER.serverName:
          http.get(boatAPI.currentModelYear).then(({ data }) => {
            this.setState(
              { selectedYear: storedSelectedYear || data[1] || data[0], currentYear: data[1] || data[0] },
              resolve
            );
          });
          break;
        case ROLES.COMPONENT_MANUFACTURER.serverName:
          http.get(boatAPI.currentModelYear).then(({ data }) => {
            this.setState(
              { selectedYear: storedSelectedYear || data[1] || data[0], currentYear: data[1] || data[0] },
              resolve
            );
          });
          break;
        case ROLES.TRAILER_MANUFACTURER.serverName:
          http.get(trailerAPI.currentModelYear).then(({ data }) => {
            this.setState({ selectedYear: storedSelectedYear || data, currentYear: data }, resolve);
          });
          break;
        default: {
          const currentCalendarYear = new Date().getFullYear();
          this.setState(
            { selectedYear: storedSelectedYear || currentCalendarYear, currentYear: currentCalendarYear },
            resolve
          );
          break;
        }
      }
    });
  };

  componentDidUpdate(prevProps) {
    if (prevProps.match.params.manufacturerRole !== this.props.match.params.manufacturerRole) {
      this.roleSwitch();
      this.isOilDashboard = this.props.match.params.manufacturerRole === ROLES.OIL_MANUFACTURER.nameForUrl;
      window.sessionStorage.removeItem('dashboardListState');
      window.sessionStorage.removeItem('dashboardListAdditionalState');
      this.checklistItems.forEach(item => (item.checked = false));
      this.setState({ filterState: { active: true }, additionalFilterState: {}, manufacturers: [], listRequestInProcess: true, searchString: '' }, this.filterList);

      this.setState(
        () => {
          this.getCurrentYear().then(() => {
            this.loadFromStore();
            this.updateListData();
          });
        }
      );
    }
  }

  updateListData = () => {
    const { user, match } = this.props;
    const { filterState, selectedYear } = this.state;

    const roleServerName = Object.values(ROLES).reduce((acc, role) => {
      if (match.params.manufacturerRole === role.nameForUrl) {
        return role.serverName;
      }

      return acc;
    }, null);

    if (this.isOilDashboard) {
      new Promise((res, rej) => {
        Promise.all([
          http.get(manufacturerAPI.listWithChecklistItems(ROLES.OIL_MANUFACTURER.serverName, selectedYear)),
          http.get(
            manufacturerAPI.listWithChecklistItems(
              ROLES.OIL_BRANDER.serverName,
              selectedYear,
              filterState.brandSignStatus
            )
          ),
        ]).then(result => {
          Store.insert(
            roleServerName,
            this.addDBKeyToEntries(
              this.createOilListWithCombinedChecklistData(result[1].data, result[0].data),
              selectedYear
            ),
            selectedYear.toString()
          ).then(() => {
            this.setState({ listRequestInProcess: false });
            this.loadFromStore();
          });
        });
      });
    } else {
      new Promise((res, rej) => {
        http
          .get(
            manufacturerAPI.listWithChecklistItems(
              roleServerName,
              selectedYear,
              false,
              user.getInspectorCode(),
              user.activeRole.serverName === ROLES.BOAT_MANUFACTURER.serverName
            )
          )
          .then(({ data }) => {
            Store.insert(roleServerName, this.addDBKeyToEntries(data, selectedYear), selectedYear.toString()).then(
              () => {
                this.setState({ listRequestInProcess: false });
                this.loadFromStore();
                res();
              }
            );
          });
      });
    }
  };

  addDBKeyToEntries = (list, selectedYear) => {
    list.forEach(entry => (entry.dbKey = `${entry.code}_${selectedYear}`));

    return list;
  };

  createOilListWithCombinedChecklistData = (brandersList, formulatorsList) => {
    const { filterState } = this.state;

    formulatorsList.forEach(formulator => {
      let matchedBranderEntry = brandersList.find(brander => brander.name === formulator.name);
      if (matchedBranderEntry) {
        matchedBranderEntry.checklists = [formulator.checklist, matchedBranderEntry.checklist];
        matchedBranderEntry.checklist = formulator.checklist;
      } else if (!filterState.brandSignStatus) {
        brandersList.push(formulator);
      }
    });
    return brandersList;
  };

  loadFromStore = () => {
    const { user } = this.props;
    const { selectedYear } = this.state;
    this.allManufacturers = [];

    Store.select(user.getActiveRole(), selectedYear.toString()).then(data => {
      this.allManufacturers = data;
      this.initCheckListItems(data);
      this.filterList();
      this.sortList();
    });
  };

  initCheckListItems = data => {
    let allPossibleChecklistItemNamesArray = {};
    this.isWithPaymentStatus = false;
    this.isWithCertificationStatus = false;

    const addChecklistItemsFromCurrentEntryToFilterList = entry => {
      if (entry.hasCertification) {
        this.isWithCertificationStatus = true;
        allPossibleChecklistItemNamesArray.Certification = true;
      }

      entry.itemValues.forEach(item => {
        allPossibleChecklistItemNamesArray[item.name] = true;
        if (item.statusType === 'PAYMENT') {
          this.isWithPaymentStatus = true;
        }
      });
    };

    data.forEach(manufacturerEntry => {
      if (manufacturerEntry.checklists) {
        manufacturerEntry.checklists.forEach(checklist => {
          addChecklistItemsFromCurrentEntryToFilterList(checklist);
        });
      } else {
        addChecklistItemsFromCurrentEntryToFilterList(manufacturerEntry.checklist);
      }
    });

    this.checklistItems = Object.keys(allPossibleChecklistItemNamesArray).map(checklistItemName => {
      return {
        name: checklistItemName,
        checked: false,
      };
    });
  };

  handleYearSelect = selectedYear => {
    const { user } = this.props;

    user.saveSelectedYear(selectedYear);

    this.setState({ selectedYear, listRequestInProcess: true, manufacturers: [] }, () => {
      this.loadFromStore();
      this.updateListData();
    });
  };

  handleSortClick = field => () => {
    this.setState(prevState => {
      return {
        sortedBy: field,
        sortOrder: prevState.sortedBy === field ? -prevState.sortOrder : prevState.sortOrder,
        activePage: 1,
      };
    }, this.sortList);
  };

  sortList = () => {
    const { sortedBy, sortOrder } = this.state;
    let { manufacturers } = this.state;

    manufacturers.sort((a, b) => {
      switch (sortedBy) {
        case 'isCertified':
          return a.checklist.isCertified > b.checklist.isCertified ? sortOrder : -sortOrder;
        case 'isPaid': {
          const paymentStatusA = a.checklist.itemValues.find(checklistItem => checklistItem.statusType === 'PAYMENT');
          const paymentStatusB = b.checklist.itemValues.find(checklistItem => checklistItem.statusType === 'PAYMENT');
          return (paymentStatusA && paymentStatusA.isChecked) > (paymentStatusB && paymentStatusB.isChecked)
            ? sortOrder
            : -sortOrder;
        }
        case 'active':
          return a.active > b.active ? sortOrder : -sortOrder;
        case 'allRoles': {
          let aRoleSummaryWeight = 0;
          let bRoleSummaryWeight = 0;
          if (a.allRoles.indexOf(ROLES.OIL_MANUFACTURER.serverName) !== -1) aRoleSummaryWeight += 2;
          if (a.allRoles.indexOf(ROLES.OIL_BRANDER.serverName) !== -1) aRoleSummaryWeight += 1;
          if (b.allRoles.indexOf(ROLES.OIL_MANUFACTURER.serverName) !== -1) bRoleSummaryWeight += 2;
          if (b.allRoles.indexOf(ROLES.OIL_BRANDER.serverName) !== -1) bRoleSummaryWeight += 1;
          return aRoleSummaryWeight > bRoleSummaryWeight ? sortOrder : -sortOrder;
        }
        default:
          return a[sortedBy].toLowerCase().replace(/\s+/g, ' ') > b[sortedBy].toLowerCase().replace(/\s+/g, ' ')
            ? sortOrder
            : -sortOrder;
      }
    });
    this.setState({ manufacturers });
  };

  handleRowSelect = (rowsCountSelected, totalRowsCount) => {
    const rowsCount = rowsCountSelected && rowsCountSelected < totalRowsCount ? rowsCountSelected : 0;

    this.setState({ rowsCount, activePage: 1 });
  };

  handlePaginationClick = activePage => {
    this.setState({ activePage });
  };

  handleSelectFilter = filterType => filterElement => {
    const { filterState } = this.state;

    let newFilterState = { ...filterState };

    if (filterElement.value === 'All') delete newFilterState[filterType];
    else newFilterState[filterType] = filterElement.value;

    if (filterType === 'brandSignStatus' && filterElement !== 'All') {
      newFilterState.allRoles = ROLES.OIL_BRANDER.serverName;
      this.setState({ filterState: newFilterState }, this.updateListData);
    } else if (
      filterType === 'allRoles' &&
      filterElement.value !== ROLES.OIL_BRANDER.serverName &&
      newFilterState.brandSignStatus !== undefined
    ) {
      delete newFilterState.brandSignStatus;
      this.setState({ filterState: newFilterState }, this.updateListData);
    } else {
      this.setState({ filterState: newFilterState }, this.filterList);
    }
  };

  handleSelectAdditionalFilter = filterItem => {
    const { additionalFilterState } = this.state;
    let newFilterState = { ...additionalFilterState };
    const filterItemName = filterItem.name;
    const { user } = this.props;

    if (newFilterState[filterItemName] === undefined) {
      this.checklistItems.find(item => item.name === filterItemName).checked = true;
      newFilterState[filterItemName] = true;
    } else if (filterItemName === 'searchString') {
      delete newFilterState[filterItemName];
    } else {
      this.checklistItems.find(item => item.name === filterItemName).checked = false;
      delete newFilterState[filterItemName];
      //user.updateURLSearchString({searchString: null});
      //this.setState({ searchString: null });
      this.handleSearchStringInput('');
    }

    this.setState({ additionalFilterState: newFilterState }, this.filterList);
  };

  toggleAdditionalFilterItem = filterItemName => () => {
    let { additionalFilterState } = this.state;

    additionalFilterState[filterItemName] = !additionalFilterState[filterItemName];
    this.setState({ additionalFilterState }, this.filterList);
  };

  clearAdditionalFilter = () => {
    window.sessionStorage.removeItem('dashboardListState');
    window.sessionStorage.removeItem('dashboardListAdditionalState');
    this.checklistItems.forEach(item => (item.checked = false));
    this.setState({ filterState: { active: true }, additionalFilterState: {}, manufacturers: [], listRequestInProcess: true }, this.filterList);
  };

  handleSearchStringInput = value => {
    const { user } = this.props;
    this.setState({ searchString: value, activePage: 1 }, () => {
      this.filterList();
      user.updateURLSearchString({ searchString: value });
    });
  };

  filterList = () => {
    const { user } = this.props;
    const { filterState, searchString, additionalFilterState } = this.state;

    const searchableFields = getSearchableFields(user.activeRole.serverName === ROLES.BOAT_MANUFACTURER.serverName);

    let manufacturers = [...this.allManufacturers];

    const isMatchingSearchString = manufacturer =>
      searchableFields
        .map(({ fieldName, nestedFieldName, isArrayOfObjects }) =>
          isArrayOfObjects
            ? manufacturer[fieldName].some(({ [nestedFieldName]: fieldName }) =>
              isStringIncludes(fieldName, searchString)
            )
            : isStringIncludes(manufacturer[fieldName].toString(), searchString)
        )
        .some(field => field);

    Object.keys(filterState).forEach(filterFieldName => {
      switch (filterFieldName) {
        case 'allRoles':
          manufacturers = manufacturers.filter(
            element => element[filterFieldName].indexOf(filterState[filterFieldName]) !== -1
          );
          break;
        case 'brandSignStatus':
          break;
        default:
          manufacturers = manufacturers.filter(element => element[filterFieldName] === filterState[filterFieldName]);
      }
    });

    if (searchString) manufacturers = manufacturers.filter(isMatchingSearchString);

    Object.keys(additionalFilterState).forEach(additionalFilterFieldName => {
      manufacturers = manufacturers.filter(manufacturerEntry => {
        switch (additionalFilterFieldName) {
          case 'Certification':
            if (manufacturerEntry.checklists) {
              if (filterState.allRoles) {
                const checklistToFilterBy = manufacturerEntry.checklists.find(
                  entry => entry.manufacturerRole === filterState.allRoles
                );
                const checklistItem = checklistToFilterBy.isCertified;
                return checklistItem !== undefined
                  ? checklistItem === additionalFilterState[additionalFilterFieldName]
                  : !additionalFilterState[additionalFilterFieldName];
              } else {
                let filterPassed = false;
                manufacturerEntry.checklists.forEach(checklist => {
                  const checklistItem = checklist.isCertified;
                  if (
                    checklistItem !== undefined
                      ? checklistItem === additionalFilterState[additionalFilterFieldName]
                      : !additionalFilterState[additionalFilterFieldName]
                  )
                    filterPassed = true;
                });
                return filterPassed;
              }
            } else {
              const checklistItem = manufacturerEntry.checklist.isCertified;
              return checklistItem !== undefined
                ? checklistItem === additionalFilterState[additionalFilterFieldName]
                : !additionalFilterState[additionalFilterFieldName];
            }
          default:
            if (manufacturerEntry.checklists) {
              if (filterState.allRoles) {
                const checklistToFilterBy = manufacturerEntry.checklists.find(
                  entry => entry.manufacturerRole === filterState.allRoles
                );
                const checklistItem = checklistToFilterBy.itemValues.find(
                  checklistItem => checklistItem.name === additionalFilterFieldName
                );
                return checklistItem
                  ? checklistItem.isChecked === additionalFilterState[additionalFilterFieldName]
                  : !additionalFilterState[additionalFilterFieldName];
              } else {
                let filterPassed = false;
                manufacturerEntry.checklists.forEach(checklist => {
                  const checklistItem = checklist.itemValues.find(
                    checklistItem => checklistItem.name === additionalFilterFieldName
                  );
                  if (
                    checklistItem
                      ? checklistItem.isChecked === additionalFilterState[additionalFilterFieldName]
                      : !additionalFilterState[additionalFilterFieldName]
                  )
                    filterPassed = true;
                });
                return filterPassed;
              }
            } else {
              const checklistItem = manufacturerEntry.checklist.itemValues.find(
                checklistItem => checklistItem.name === additionalFilterFieldName
              );
              return checklistItem
                ? checklistItem.isChecked === additionalFilterState[additionalFilterFieldName]
                : !additionalFilterState[additionalFilterFieldName];
            }
        }
      });
    });

    this.setState({ manufacturers }, this.sortList);
  };

  handleSelectManufacturer = manufacturer => () => {
    const { user } = this.props;

    if (this.isOilDashboard) {
      if (manufacturer.allRoles.indexOf(ROLES.OIL_MANUFACTURER.serverName) !== -1)
        user.setActiveRole(ROLES.OIL_MANUFACTURER.serverName);
      else user.setActiveRole(ROLES.OIL_BRANDER.serverName);
    }

    user.selectManufacturer(manufacturer);
  };

  handleStatusChangeClick = manufacturer => event => {
    stopPropagation(event);

    this.$modalBody = `Are you sure you want to ${
      manufacturer.active ? 'Deactivate' : 'Reactivate'
    } this manufacturer?`;
    this.$confirmModalAction = this._toggleStatus(manufacturer);
    this.setState({ isModalOpen: true });
  };

  _toggleStatus = manufacturer => () => {
    const { requestInProcess } = this.state;

    if (requestInProcess) return null;

    this.setState({ requestInProcess: true }, () => {
      http
        .put(manufacturerAPI.changeStatus(manufacturer.code, !manufacturer.active))
        .then(({ data }) => {
          this._updateListEntry(manufacturer, data);
          NotificationManager.success(1, 'The status was updated successfully.');
        })
        .finally(() => {
          this.setState({ requestInProcess: false });
          this._closeModal();
        });
    });
  };

  _updateListEntry = (oldEntry, newEntry) => {
    const { user } = this.props;
    let { manufacturers } = this.state;

    newEntry.checklist = oldEntry.checklist;
    if (oldEntry.checklists) newEntry.checklists = oldEntry.checklists;

    const oldEntryIndex = manufacturers.indexOf(oldEntry);
    manufacturers.splice(oldEntryIndex, 1, newEntry);

    Store.insert(user.getActiveRole(), newEntry);
    this.setState({ manufacturers });
  };

  _closeModal = () => {
    this.setState({ isModalOpen: false });
  };

  render() {
    const {
      activePage,
      additionalFilterState,
      currentYear,
      filterState,
      isModalOpen,
      manufacturers,
      requestInProcess,
      rowsCount,
      sortedBy,
      sortOrder,
      searchString,
      listRequestInProcess,
      selectedYear,
    } = this.state;

    const { user } = this.props;

    const tableColumnsWidths = this.isOilDashboard
      ? {
        code: '_width_pc_10',
        allRoles: '_width_pc_20',
        isCertified: '_width_pc_12',
        active: '_width_pc_10',
        isPaid: '_width_pc_10',
        isComplete: '_width_pc_10',
        actions: '_width_pc_12',
      }
      : {
        code: '_width_pc_10',
        isCertified: '_width_pc_12',
        active: '_width_pc_10',
        isPaid: '_width_pc_10',
        isComplete: '_width_pc_10',
        actions: '_width_pc_12',
      };

    return (
      <React.Fragment>
        <section className='scene__scene-wrapper'>
          <section className='scene__content-wrapper list-scene__content-wrapper dashboard-scene'>
            <div className='scene__scene-header list-scene__scene-header'>
              {`${user.getRoleName().split(' ')[0]} Manufacturers`}
              <Input
                mainWrapperClassName='list-scene__header-search-field-wrapper'
                styleClass={inputStyleClasses.searchBar}
                placeholder='Type here to search'
                maxLength={250}
                value={searchString}
                onChange={this.handleSearchStringInput}
              />
            </div>
            <Header
              handleSelectFilter={this.handleSelectFilter}
              handleBrandSignStatusSelect={this.handleBrandSignStatusSelect}
              user={user}
              filterState={filterState}
              rowsCount={rowsCount}
              outCount={manufacturers.length}
              handleRowCountSelect={this.handleRowSelect}
              isOilDashboard={this.isOilDashboard}
              checklistItems={this.checklistItems || []}
              additionalFilterState={additionalFilterState}
              handleSelectAdditionalFilter={this.handleSelectAdditionalFilter}
              toggleAdditionalFilterItem={this.toggleAdditionalFilterItem}
              clearAdditionalFilter={this.clearAdditionalFilter}
              isWithPaymentStatus={this.isWithPaymentStatus}
              isWithCertificationStatus={this.isWithCertificationStatus}
              selectedYear={selectedYear}
              currentYear={currentYear}
              handleYearSelect={this.handleYearSelect}
              listRequestInProcess={listRequestInProcess}
            />
            <TableHeader
              user={user}
              handleSortClick={this.handleSortClick}
              order={sortOrder === 1}
              sortedBy={sortedBy}
              isOilDashboard={this.isOilDashboard}
              tableColumnsWidths={tableColumnsWidths}
              isWithPaymentStatus={this.isWithPaymentStatus}
              isWithCertificationStatus={this.isWithCertificationStatus}
            />
            <TableBody
              noEntrysForThisManufacturer={!this.allManufacturers.length}
              listRequestInProcess={listRequestInProcess}
              manufacturers={manufacturers}
              user={user}
              handleSelectManufacturer={this.handleSelectManufacturer}
              rowsCount={rowsCount}
              activePage={activePage}
              isOilDashboard={this.isOilDashboard}
              tableColumnsWidths={tableColumnsWidths}
              handleStatusChangeClick={this.handleStatusChangeClick}
              sortedBy={sortedBy}
              isWithPaymentStatus={this.isWithPaymentStatus}
              isWithCertificationStatus={this.isWithCertificationStatus}
            />
            <Footer
              handleClick={this.handlePaginationClick}
              handleSelect={this.handleRowSelect}
              activePage={activePage}
              rowsCount={rowsCount}
              outCount={manufacturers.length}
            />
          </section>
        </section>
        <Modal isOpen={isModalOpen}>
          <div className='ect-modal-header'>Confirmation</div>
          <div className='ect-modal-body'>{this.$modalBody}</div>
          <div className='ect-modal-footer'>
            <Button
              label='No'
              buttonClassName='_width_135 _height_40'
              onClick={this._closeModal}
              color={buttonColor.gray}
              type={buttonType.secondary}
            />
            <Button
              label='Yes'
              disabledWithSpinner={requestInProcess}
              buttonClassName='_width_135 _height_40'
              onClick={this.$confirmModalAction}
            />
          </div>
        </Modal>
      </React.Fragment>
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/Scenes/DashboardScene/index.jsx